import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Avatar, Box, FadeInWithDrawerView, HStack, Image, IconButton, ScrollView, Text, VFlexPropagate, VStack, useContrastProps } from '~/native-ui';
import getConfig from '~/config';

import { AppBarMenuContextProvider } from './AppBarMenuContextProvider';

const HeaderAvatar = React.memo(({bg = "transparent", source, children, ...props}) => {
    const [hasControlledSource, setHasControlledSource] = React.useState(!!source)
    const componentProps = {
        bg,
        height: "46px",
        width: "46px",
        borderRadius: "2",
        _text: React.useMemo(() => ({
            fontSize: "md",
        }), []),
        resizeMode: 'contain',
        ...props
    };

    React.useEffect(() => {
        setHasControlledSource(!!source);
    }, [source]);


    const imageProps = {
        source,
        resizeMode: "contain",
        style: React.useMemo(() => ({
            borderRadius: 50,
            height: 46,
            width: 46,
        }), []),
        onError: React.useCallback(() => {
            setHasControlledSource(false);
        }, [setHasControlledSource])
    }




    if (hasControlledSource) {
        return <Image {...imageProps} />
    }

    return <Avatar {...componentProps} children={children} />
});


const AppBarSection = React.memo((props) => {
    const boxProps = {
        ...props,
        ...useContrastProps()
    }
    return <Box {...boxProps} />
})


const VersionMeta = ({trans}) => {
    const versions = [{ label: trans?.("versions.admin"), version: getConfig().version }]
        .map((item) => ( <HStack key={item.label}><Text fontSize="xs">{item.label}: <Text bold fontSize="xs">v{item.version}</Text></Text></HStack> ));

    return (
        <VStack opacity="50" space={1}>
            { versions }
        </VStack>
    )
}


const factory = IconButton => {
    class AppBar extends React.Component {
        static propTypes = {
            companyTitle: PropTypes.string,
            userMeta: PropTypes.shape({
                email: PropTypes.string,
                name: PropTypes.string,
                avatar: PropTypes.string,
            }),
            showAsHeader: PropTypes.bool,
            children: PropTypes.node,
            className: PropTypes.string,
            fixed: PropTypes.bool,
            flat: PropTypes.bool,
            leftIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            onLeftIconClick: PropTypes.func,
            onRightIconClick: PropTypes.func,
            rightIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            scrollHide: PropTypes.bool,
            title: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
        };

        static defaultProps = {
            className: '',
            fixed: false,
            flat: false,
            scrollHide: false,
        };

        state = { hidden: false, height: 0 };

        logo = { uri: this.props.logo, }

        componentDidMount() {
            if (this.props.scrollHide) {
                this.initializeScroll();
            }

            this.initializeAnimations();
        }

        componentWillReceiveProps(nextProps) {
            if (!this.props.scrollHide && nextProps.scrollHide) {
                this.initializeScroll();
            }

            if (this.props.animations && !nextProps.animations) {
                this.initializeAnimations();
            }

            if (this.props.logo !== nextProps.logo) {
                this.logo = { uri: this.props.logo, }
            }
        }

        componentWillUnmount() {
            if (this.props.scrollHide) {
                this.endScroll();
            }

            this.destructAnimations();
        }


        removeAnimations = null;
        initializeAnimations() {
            const { animation } = this.props.animationContext || {};
            this.destructAnimations()

            if (!animation) {
                return;
            }
        }


        destructAnimations() {
            this.animation?.();
            this.removeAnimations = null;
        }



        initializeScroll() {
            window.addEventListener('scroll', this.handleScroll);
            const { height } = this.rootNode.getBoundingClientRect();
            this.curScroll = window.scrollY;
            this.setState({ height });
        }

        endScroll() {
            window.removeEventListener('scroll', this.handleScroll);
        }

        handleScroll = () => {
            const scrollDiff = this.curScroll - window.scrollY;
            const hidden = scrollDiff < 0 &&
                window.scrollY !== undefined &&
                window.scrollY > this.state.height;
            this.setState({ hidden });
            this.curScroll = window.scrollY;
        };

        renderHeaderChildren() {
            const theme = this.props.theme || {};
            return (
                <React.Fragment>
                    <div className={theme.inner}>
                        <div className={theme.logoContainer} onClick={this.props.onClickLogo}>
                            <img src={logo} />
                        </div>
                        <div className={theme.spacer}></div>

                        <div className={theme.rightContainer}>
                            {right}
                        </div>
                    </div>
                    <div className={theme.centerOverlay}>
                        <div className={theme.centerContainer}>
                            {children}
                        </div>
                    </div>
                </React.Fragment>
            )
        }

        renderLogo() {
            const { userMeta, companyTitle, logo } = this.props;
            return (
                <AppBarSection p="3">
                    <HStack alignItems="center" space="2" className={this.props.theme?.logoContainer} cursor="pointer" onClick={this.props.onClickLogo}>
                        <HeaderAvatar source={this.logo}>
                            {companyTitle?.[0]?.toUpperCase()}
                        </HeaderAvatar>
                        <FadeInWithDrawerView>
                            <VStack space="0">
                                <Box _text={{ color: "inherit" }}>{ companyTitle }</Box>
                                <VersionMeta trans={ this.props.trans } />
                            </VStack>
                        </FadeInWithDrawerView>
                    </HStack>
                </AppBarSection>
            )
        }


        renderCenterMenu() {
            if (!this.props.children) {
                return null;
            }

            return (
                <Box>
                    {this.props.children}
                </Box>
            )
        }


        renderRightMenu() {
            if (!this.props.right) {
                return null;
            }

            return (
                <Box>
                    {this.props.right}
                </Box>
            )
        }

        renderFooter() {
            return (
                <Box>
                    {this.props.footer}
                </Box>
            )
        }


        renderHeader() {
            const { theme } = this.props.state;

            const className = cn(theme.appBar, {
                [theme.fixed]: this.props.fixed,
                [theme.flat]: this.props.flat,
                [theme.scrollHide]: this.state.hidden,
            }, this.props.className);

            return (
                <header
                    className={className}
                    ref={node => {
                        this.rootNode = node;
                    }}
                >
                    <div className={theme.inner}>
                        <div className={this.props.theme.logoContainer} onClick={this.props.onClickLogo}>
                            <img src={this.props.logo} />
                        </div>
                        <div className={theme.spacer}></div>

                        
                        <div className={this.props.theme.rightContainer}>
                            {this.props.right}
                        </div>
                    </div>
                    
                <div className={theme.centerOverlay}>
                    <div className={theme.centerContainer}>
                        {this.props.children}
                    </div>
                </div>
                </header>
            )
        }

        contentContainerStyle={ flex: 1 }
        renderDrawer() {
            return (
                <AppBarSection h="100%">
                    { this.renderLogo() }
                    <ScrollView contentContainerStyle={this.contentContainerStyle}>
                        <AppBarMenuContextProvider>
                            <Box>{ this.renderCenterMenu() }</Box>
                            <Box mt="auto">{ this.renderRightMenu() }</Box>
                            { this.renderFooter() }
                        </AppBarMenuContextProvider>
                    </ScrollView>
                </AppBarSection>
            );
        }


        render() {            
            const appBar = this.props.showAsHeader ? this.renderHeader() : this.renderDrawer()

            return (
                <VFlexPropagate h="100%" flexShrink={0} flexGrow={0}>
                    { appBar }
                </VFlexPropagate>
            );
        }


    }

    return AppBar;
};

const AppBar = factory(IconButton);
// export default AppBar;
// export { factory as appBarFactory };
export { AppBar as AppBarComponent };
